import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AGGREGATE_NAMES, CHAT_COMMANDS, EVENT_CHAIN_STATUS, MUTATION_KEYS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { APICommunicator } from '~/api/APICommunicator';
import { invalidateInboxUnreadQueries } from '~/features/whatsapp/utils/invalidateInboxUnreadQueries';
import { toastChatCommandError } from '~/features/whatsapp/utils/toastChatCommandError';
import { useChainAwareInvalidation } from '~/hooks/useChainAwareInvalidation';
import useUserInfo from '~/hooks/useUserInfo';
import { queryKeys } from '~/utils/queryKeys';

type Variables = { archive: boolean };

export const useArchiveChatMutation = (chatId: string) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const user = useUserInfo();
    const invalidateOnChainComplete = useChainAwareInvalidation();

    return useMutation({
        mutationFn: ({ archive }: Variables) =>
            APICommunicator.sendCommand({
                aggregateId: chatId,
                aggregateName: AGGREGATE_NAMES.CHAT,
                commandName: archive ? CHAT_COMMANDS.markChatResolved : CHAT_COMMANDS.markChatUnresolved,
                payload: {},
            }),
        mutationKey: [MUTATION_KEYS.ARCHIVE_CHAT, chatId],
        onError: (error, { archive }) => {
            toastChatCommandError(
                error,
                t,
                archive ? 'WHATSAPP_CHAT.ERROR.RESOLVE_FAILED' : 'WHATSAPP_CHAT.ERROR.UNRESOLVE_FAILED',
            );
        },
        // Also on error: a stale click (already archived / reopened) must let the list catch up.
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.getChats() });
            if (user) invalidateInboxUnreadQueries(queryClient, user.id);
        },
        onSuccess: ({ id: correlationId }, { archive }) => {
            toast.success(t(archive ? 'WHATSAPP_CHAT.MENU.ARCHIVE_SUCCESS' : 'WHATSAPP_CHAT.MENU.UNARCHIVE_SUCCESS'));
            invalidateOnChainComplete({
                correlationId,
                onComplete: (status) => {
                    if (status === EVENT_CHAIN_STATUS.COMPLETED) return;
                    toast.warn(t('WHATSAPP_CHAT.ERROR.INBOX_REFRESH_PENDING'));
                },
                queryKeys: user
                    ? [queryKeys.getChats(), queryKeys.getInboxUnreadSummary(user.id)]
                    : [queryKeys.getChats()],
            });
        },
    });
};
